import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function About() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "name": "About MY AVA Farms - Black Soldier Fly Farming Experts in Ghana",
        "description": "Learn about MY AVA Farms' mission to revolutionize sustainable agriculture in Ghana through Black Soldier Fly farming, organic waste management, and circular economy practices in Accra",
        "url": `${siteUrl}/about`,
        "mainEntity": {
          "@type": "Organization",
          "name": "MY AVA Farms",
          "foundingDate": "2019",
          "description": "Leading Black Soldier Fly farming operation in Ghana specializing in sustainable insect protein production, organic fertilizer, and BSF training programs",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "33 Kwame Nkrumah Ave, Adabraka",
            "addressLocality": "Accra",
            "addressCountry": "GH"
          },
          "telephone": "+233233371378",
          "email": "myavagh@gmail.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/3ca3a64fdbdd1a27f61611d5a6ab11d4/19d8f85f961deb0bcb56f566b071f178.jpeg"
          }
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "About", "item": `${siteUrl}/about` }
        ]
      }
    ]
  };

  return (
    <>
      <SEO
        title="About MY AVA Farms | BSF Farming Experts in Accra, Ghana"
        description="Learn about MY AVA Farms' mission to revolutionize sustainable agriculture in Ghana through Black Soldier Fly farming, organic waste management, and circular economy practices. Leading BSF farming experts in Accra Ghana."
        keywords="MY AVA Farms, Black Soldier Fly farming Ghana, sustainable agriculture Accra, BSF experts Ghana, insect farming Ghana, organic waste management Accra, circular economy Ghana"
        ogImage="https://public.readdy.ai/ai/img_res/d8800765b2f64869fbff4ce49c372215.jpg"
        schema={schema}
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/edited_c5604001bdfff1cc3f2aa3e9e8062795_f8c29f62.jpg)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 w-full">
          <div className="text-center">
            <span className="inline-flex items-center bg-green-600/80 backdrop-blur-sm text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest mb-6">
              <i className="ri-leaf-line mr-2"></i>Proudly Ghanaian · Est. 2019
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              About MY AVA Farms
            </h1>
            <p className="text-xl md:text-2xl text-green-100 max-w-4xl mx-auto leading-relaxed">
              Pioneering sustainable agriculture through innovative Black Soldier Fly farming solutions
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                At MY AVA Farms, we are redefining sustainable agriculture through the power of the Black Soldier Fly (BSF). Our mission is to turn organic waste into valuable resources — high-quality protein feed, organic fertilizer, and eco-friendly solutions that support livestock, poultry, and aquaculture industries.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                From farming to production and training, we are committed to promoting circular economy practices that protect the environment while boosting food security.
              </p>
              <p className="text-lg text-gray-600">
                At MY AVA, we believe in innovation, sustainability, and empowering farmers with the knowledge and tools to build a greener future.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20sustainable%20insect%20farming%20facility%20with%20Black%20Soldier%20Fly%20larvae%20processing%20equipment%2C%20clean%20industrial%20environment%20with%20workers%20in%20protective%20gear%2C%20bright%20natural%20lighting%2C%20eco-friendly%20agricultural%20technology%2C%20professional%20farming%20operation&width=600&height=400&seq=about-mission&orientation=landscape"
                alt="MY AVA Farms facility"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at MY AVA Farms
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-leaf-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Sustainability</h3>
              <p className="text-gray-600">
                We prioritize environmental protection through circular economy practices that transform waste into valuable resources.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Innovation</h3>
              <p className="text-gray-600">
                We embrace cutting-edge BSF farming technologies and continuously improve our processes for maximum efficiency.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Empowerment</h3>
              <p className="text-gray-600">
                We empower farmers with knowledge, tools, and training to build sustainable agricultural practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Impact
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Creating positive change across Ghana and beyond through sustainable agriculture
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">500+</div>
              <div className="text-gray-600">Farmers Trained</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">10,000</div>
              <div className="text-gray-600">Tons Waste Processed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">50+</div>
              <div className="text-gray-600">Partner Farms</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">95%</div>
              <div className="text-gray-600">Waste Reduction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experienced professionals dedicated to sustainable agriculture innovation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <img 
                src="https://static.readdy.ai/image/3ca3a64fdbdd1a27f61611d5a6ab11d4/a2fa57781278bd2d75c7afa4a4236dac.png"
                alt="CEO"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Godfred Davidson</h3>
              <p className="text-green-600 mb-4">CEO and Founder</p>
              <p className="text-gray-600 text-sm">
                15+ years in sustainable agriculture and insect farming innovation
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <img 
                src="https://static.readdy.ai/image/3ca3a64fdbdd1a27f61611d5a6ab11d4/32b6fc06b8cd37356519ad923e0c08af.jpeg"
                alt="CTO"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Annie Yeboah</h3>
              <p className="text-blue-600 mb-4">Snr Admin & Operations Officer</p>
              <p className="text-gray-600 text-sm">
                Expert in Business Administration, BSF cultivation technology and waste processing systems
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <img 
                src="https://static.readdy.ai/image/3ca3a64fdbdd1a27f61611d5a6ab11d4/02089b5289a2f8f3e8ee33a7c216ae22.jpeg"
                alt="Senior Partner and Consultant"
                className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Samuel Boadu</h3>
              <p className="text-purple-600 mb-4">Snr Partner and Consultant</p>
              <p className="text-gray-600 text-sm">
                Specialist in large-scale BSF production and farmer training programs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Join Us in Building a Sustainable Future
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Partner with MY AVA Farms to transform your agricultural practices and contribute to a greener tomorrow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/products')}
              className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Explore Our Solutions
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact Us Today
            </button>
          </div>
        </div>
      </section>
    </>
  );
}